// src/utils/careerLogic.js

export const STATUS = {
  NO_CURSADA: "NO_CURSADA",
  PUEDE_CURSAR: "PUEDE_CURSAR",
  CURSANDO: "CURSANDO",
  CURSADA_APROBADA: "CURSADA_APROBADA", // ÚNICO estado final de aprobación
};

/**
 * @param {any} planCarrera
 * @param {string} materiaId
 * @returns {any|null}
 */
const getMateriaFromPlan = (planCarrera, materiaId) => {
  if (!planCarrera || !planCarrera.grupos) return null;
  for (/** @type {any} */ const grupo of planCarrera.grupos) {
    if (grupo.materias && typeof grupo.materias.find === 'function') { // Chequeo adicional
      const materiaEncontrada = grupo.materias.find((/** @type {any} */ m) => m.id === materiaId);
      if (materiaEncontrada) return materiaEncontrada;
    }
  }
  return null;
};

/**
 * @param {string} materiaId
 * @param {Record<string, any>} nuevasMateriasEstado
 * @param {any} planCarrera
 */
export const aprobarCursadasAnterioresRecursivo = (materiaId, nuevasMateriasEstado, planCarrera) => {
  const materiaDef = getMateriaFromPlan(planCarrera, materiaId);

  if (materiaDef && materiaDef.correlativas && materiaDef.correlativas.length > 0) {
    materiaDef.correlativas.forEach((/** @type {string} */ idCorrelativa) => {
      const correlativaActual = nuevasMateriasEstado[idCorrelativa];
      if (correlativaActual && 
          correlativaActual.estado !== STATUS.CURSADA_APROBADA &&
          correlativaActual.notaFinal !== -3) { 
        nuevasMateriasEstado[idCorrelativa] = {
          ...correlativaActual,
          estado: STATUS.CURSADA_APROBADA,
          notaFinal: -3, // -3 indica "cursada por arrastre"
          puedeCursar: true, // Si se aprueba por arrastre, ya puede cursar la siguiente (implícito)
        };
        aprobarCursadasAnterioresRecursivo(idCorrelativa, nuevasMateriasEstado, planCarrera);
      }
    });
  }
};

/**
 * @param {any} planCarrera
 * @returns {Record<string, any>}
 */
export const inicializarMaterias = (planCarrera) => {
  /** @type {Record<string, any>} */
  const materiasConEstado = {};
  if (!planCarrera || !planCarrera.grupos || typeof planCarrera.grupos.forEach !== 'function') {
    console.error("Error en inicializarMaterias: planCarrera.grupos no es un array o planCarrera es undefined", planCarrera);
    return materiasConEstado;
  }
  planCarrera.grupos.forEach((/** @type {any} */ grupo) => {
    if (!grupo.materias || typeof grupo.materias.forEach !== 'function') {
      console.error("Error en inicializarMaterias: grupo.materias no es un array en:", grupo.nombre, grupo);
      return;
    }
    grupo.materias.forEach((/** @type {any} */ materia) => {
      materiasConEstado[materia.id] = {
        ...materia,
        estado: STATUS.NO_CURSADA,
        // Preservar notaFinal si ya existe (por ejemplo al reimportar datos)
        notaFinal: typeof materia.notaFinal === 'number' ? materia.notaFinal : -1,
        // Nueva propiedad para registrar la fecha del examen final
        fecha: materia.fecha || null,
        puedeCursar: false,
      };
    });
  });
  return materiasConEstado;
};

// Contar CPC con CURSADA APROBADA (o Final, o arrastre de cursada) para TODAS las reglas de avance
/**
 * @param {Record<string, any>} materias
 * @returns {number}
 */
export const calcularCursadasAprobadasCPC = (materias) => {
  if (!materias) return 0;
  return Object.values(materias).filter(m => 
    m.categoria === 'CPC' && 
    (m.estado === STATUS.CURSADA_APROBADA || m.notaFinal === -3)
  ).length;
};

// Contar CPO Obligatorias Comunes ("negritas") con CURSADA APROBADA (o Final, o arrastre de cursada)
/**
 * @param {Record<string, any>} materias
 * @returns {number}
 */
export const calcularCursadasAprobadasCPONegritas = (materias) => {
    if (!materias) return 0;
    const idsNegritas = ['uba_21', 'uba_22', 'uba_23', 'uba_24', 'uba_25'];
    return Object.values(materias).filter(m => 
      idsNegritas.includes(m.id) &&
      (m.estado === STATUS.CURSADA_APROBADA || m.notaFinal === -3)
    ).length;
};

// Contar FINALES aprobados del CPC (para reglas muy específicas si las hubiera, o promedio)
/**
 * @param {Record<string, any>} materias
 * @returns {number}
 */
export const calcularFinalesAprobadosCPC = (_materias) => 0; // Obsoleto: FINAL_APROBADO eliminado

const MATERIAS_NEGRITAS_IDS = ['uba_21', 'uba_22', 'uba_23', 'uba_24', 'uba_25'];

// eslint-disable-next-line no-unused-vars
const ORIENTACIONES_REQUISITOS = {
  'Derecho Privado': ['uba_21', 'uba_25'], // Familia y Sucesiones, Internacional Privado
  'Derecho Notarial': ['uba_21', 'uba_25'], // Familia y Sucesiones, Internacional Privado
  'Derecho Penal': ['uba_15'], // Elementos de Derecho Penal y Procesal Penal
  'Derecho Empresarial': ['uba_23', 'uba_24'], // Sociedades, Finanzas
  'Derecho Tributario': ['uba_23', 'uba_24'], // Sociedades, Finanzas
  'Derecho del Trabajo': ['uba_16'], // Elementos de Derecho del Trabajo
  'Derecho Público': ['uba_17', 'uba_22'], // Elementos de Derecho Administrativo, Internacional Público
  'Derecho Internacional Público': ['uba_17', 'uba_22'], // Elementos de Derecho Administrativo, Internacional Público
};

// Materias sin prerrequisitos según resolución
// eslint-disable-next-line no-unused-vars
const MATERIAS_SIN_PRERREQUISITOS = [
  'uba_13', // Teoría del Estado
  'uba_14', // Elementos de Derecho Civil
  'uba_15', // Teoría General del Derecho
  'uba_16', // Derechos Humanos y Garantías
  'uba_17', // Elementos de Análisis Económico y Financiero
];

// Reglas especiales para flexibilidad de correlatividades
const REGLAS_ESPECIALES = {
  CPC_ONCE_MATERIAS: 11,  // Con 11 materias del CPC, se ignoran correlatividades dentro del CPC
  CPO_DOCE_MATERIAS: 12,  // Con 12 materias del CPC, se puede empezar el CPO
  CPO_DIEZ_MATERIAS: 10   // Con 10 materias del CPC, se pueden cursar las "negritas" del CPO
};

// Materias que solo requieren CBC
const MATERIAS_SOLO_CBC = [
  'uba_7',  // Teoría General del Derecho
  'uba_8',  // Teoría del Estado
  'uba_9',  // Derechos Humanos y Garantías
  'uba_11', // Elementos de Derecho Civil
  'uba_20'  // Elementos de Análisis Económico y Financiero
];

/**
 * Determina si el CBC está completo considerando el flag compacto y el estado de materias.
 * @param {Record<string, any>} materias
 * @param {boolean} cbcAprobadoFlag
 * @returns {boolean}
 */
export const isCBCCompleto = (materias, cbcAprobadoFlag) => {
  if (cbcAprobadoFlag) return true;
  if (!materias) return false;
  const aprobadas = Object.values(materias).filter(
    (m) => m.categoria === 'CBC' && (
      m.estado === STATUS.CURSADA_APROBADA ||
      m.notaFinal === -3
    )
  ).length;
  return aprobadas === 6;
};

/**
 * @param {Record<string, any>} materiasActuales
 * @param {number} cpcCursadasConteo
 * @param {number} cpcFinalesConteo
 * @param {import('./careerLogic').PuntosCPO|number} cpoPuntos
 * @returns {Record<string, any>}
 */
export const recalcularEstadoMaterias = (
    materiasActuales,
    cpcCursadasConteo, 
    cpcFinalesConteo,  
    cpoPuntos,
    cbcAprobadoFlag = false
) => {
  const nuevasMaterias = JSON.parse(JSON.stringify(materiasActuales)); 

  const cbcAprobadoConCursada = isCBCCompleto(nuevasMaterias, cbcAprobadoFlag);
  
  const puedeIniciarCPOconCursadas = cpcCursadasConteo >= REGLAS_ESPECIALES.CPO_DOCE_MATERIAS;
  const puedeIniciarNegritasConCursadas = cpcCursadasConteo >= REGLAS_ESPECIALES.CPO_DIEZ_MATERIAS;
  const cpcCasiCompleto = cpcCursadasConteo >= REGLAS_ESPECIALES.CPC_ONCE_MATERIAS;

  Object.keys(nuevasMaterias).forEach((materiaId) => {
    const materia = nuevasMaterias[materiaId];

    // No modificar estados ya establecidos
    if (materia.estado === STATUS.CURSANDO || materia.estado === STATUS.CURSADA_APROBADA) {
      materia.puedeCursar = false;
      return;
    }

    // Si es una materia que solo requiere CBC
    if (MATERIAS_SOLO_CBC.includes(materia.id)) {
      materia.puedeCursar = cbcAprobadoConCursada;
      if (materia.puedeCursar && materia.estado === STATUS.NO_CURSADA) {
        materia.estado = STATUS.PUEDE_CURSAR;
      }
      return;
    }

    let correlativasDirectasCumplidas = false;
    if (!materia.correlativas || materia.correlativas.length === 0) {
      correlativasDirectasCumplidas = true;
    } else {
      // Si tiene 11 materias del CPC y la materia es del CPC, ignorar correlatividades
      if (cpcCasiCompleto && materia.categoria === 'CPC') {
        correlativasDirectasCumplidas = true;
      } else {
        let directasOk = true;
        for (const idCorrelativa of materia.correlativas) {
          const materiaCorrelativa = nuevasMaterias[idCorrelativa];
          if (!materiaCorrelativa || !(materiaCorrelativa.estado === STATUS.CURSADA_APROBADA)) {
            directasOk = false;
            break;
          }
        }
        correlativasDirectasCumplidas = directasOk;
      }
    }

    let habilitadaPorReglasDeCiclo = true;
    if (materia.categoria === 'CPC' && !cbcAprobadoConCursada) {
      habilitadaPorReglasDeCiclo = false;
    } else if (materia.categoria && materia.categoria.startsWith('CPO')) {
      const esObligatoriaComunCPO = MATERIAS_NEGRITAS_IDS.includes(materia.id);

      if (esObligatoriaComunCPO) {
        if (!puedeIniciarNegritasConCursadas) {
          habilitadaPorReglasDeCiclo = false;
        }
      } else {
        if (!puedeIniciarCPOconCursadas) {
          habilitadaPorReglasDeCiclo = false;
        }
      }
    }

    const puedeCursarFinalmente = correlativasDirectasCumplidas && habilitadaPorReglasDeCiclo;

    materia.puedeCursar = puedeCursarFinalmente;
    
    // Actualizar estado solo si es necesario
    if (puedeCursarFinalmente && materia.estado === STATUS.NO_CURSADA) {
      materia.estado = STATUS.PUEDE_CURSAR;
    } else if (!puedeCursarFinalmente && materia.estado === STATUS.PUEDE_CURSAR) {
      materia.estado = STATUS.NO_CURSADA;
    }
  });

  return nuevasMaterias;
};


// CALCULAR PUNTOS CPO BASADO EN CURSADA APROBADA (o superior, o arrastre)
/**
 * @typedef {Object} PuntosCPO
 * @property {number} obligatoriasComunesCursadas
 * @property {number} orientacionPrincipales
 * @property {number} orientacionDerivadas
 * @property {number} constitucionalProfundizado
 * @property {number} filosofia
 * @property {number} sociales
 * @property {number} procesalObligatorio
 * @property {number} procesalLibre
 * @property {number} libresGeneralesCPO
 * @property {number} integracion
 * @property {number} practico
 * @property {number} totalCPORequerido
 * @property {number} total
 */

/**
 * @param {Record<string, any>} materias
 * @param {string} orientacionSeleccionadaId
 * @returns {PuntosCPO}
 */
export const calcularPuntosCPO = (materias, orientacionSeleccionadaId) => {
  const puntos = {
    obligatoriasComunesCursadas: 0,
    orientacionPrincipales: 0,
    orientacionDerivadas: 0,
    constitucionalProfundizado: 0,
    filosofia: 0,
    sociales: 0,
    procesalObligatorio: 0,
    procesalLibre: 0,
    libresGeneralesCPO: 0,
    integracion: 0,
    practico: 0,
    totalCPORequerido: 0,
    total: 0,
  };
  if (!materias) return puntos;

  Object.values(materias).forEach((m) => {
    const estaAprobadaOCursada =
      m.estado === STATUS.CURSADA_APROBADA ||
      m.notaFinal === -3 ||
      m.notaFinal === -2;

    if (estaAprobadaOCursada && m.categoria && m.categoria.startsWith('CPO')) {
      const creds = Number(m.creditos || 0);

      if (m.categoria === 'CPO Obligatoria Común') {
        puntos.obligatoriasComunesCursadas += 1;
      } else if (
        orientacionSeleccionadaId &&
        (m.categoria === `CPO Orientación ${orientacionSeleccionadaId}` ||
          m.categoria === `CPO Orientación ${orientacionSeleccionadaId} - Principal` ||
          m.categoria === `CPO Orientación ${orientacionSeleccionadaId} - Principal (Opción)`)
      ) {
        puntos.orientacionPrincipales += creds;
      } else if (
        orientacionSeleccionadaId &&
        (m.categoria === `CPO Orientación ${orientacionSeleccionadaId} - Derivada` ||
          m.categoria === `CPO Orientación ${orientacionSeleccionadaId} - Derivada (Condicional)`)
      ) {
        puntos.orientacionDerivadas += creds;
      } else if (m.categoria === 'CPO Área Constitucional Profundizado') {
        puntos.constitucionalProfundizado += creds;
      } else if (m.id === 'cpo_derecho_integracion') {
        puntos.integracion += creds;
      } else if (m.categoria === 'CPO Departamento Filosofía') {
        puntos.filosofia += creds;
      } else if (m.categoria === 'CPO Departamento Sociales') {
        puntos.sociales += creds;
      } else if (
        m.id === 'cpo_procesal_concursos' ||
        m.id === 'cpo_procesal_metodos_alternativos'
      ) {
        puntos.procesalObligatorio += creds;
      } else if (m.categoria === 'CPO Departamento Procesal' && m.id.includes('_libre')) {
        puntos.procesalLibre += creds;
      } else if (m.categoria === 'CPO Libre Elección') {
        puntos.libresGeneralesCPO += creds;
      } else if (m.categoria && m.categoria.startsWith('CPO Orientación') && !m.categoria.includes(orientacionSeleccionadaId)) {
        // Materias de otras orientaciones suman a Libres
        puntos.libresGeneralesCPO += creds;
      } else if (m.categoria === 'CPO Práctica Profesional') {
        puntos.practico += creds;
      }
    }
  });

  puntos.totalCPORequerido =
    Math.min(puntos.orientacionPrincipales, 12) +
    Math.min(puntos.orientacionDerivadas, 8) +
    Math.min(puntos.constitucionalProfundizado, 4) +
    Math.min(puntos.integracion, 4) +
    Math.min(puntos.filosofia, 4) +
    Math.min(puntos.sociales, 4) +
    Math.min(puntos.procesalObligatorio, 4) +
    Math.min(puntos.procesalLibre, 2) +
    Math.min(puntos.libresGeneralesCPO, 8) +
    Math.min(puntos.practico, 19);
  puntos.total = puntos.totalCPORequerido;

  return puntos;
};

/**
 * @param {string} idCompleto
 * @returns {string}
 */
export const getNombreOrientacionDesdeId = (idCompleto) => {
    if (!idCompleto) return "";
    if (idCompleto.startsWith('CPO Orientación ') || idCompleto.startsWith('CPO - Orientación en ')) {
        let nombre = idCompleto.replace('CPO Orientación ', '').replace('CPO - Orientación en ', '');
        return nombre.split(' - ')[0].split(' (')[0].trim();
    }
    return idCompleto.trim();
};

/**
 * @param {Record<string, any>} materias
 * @returns {string}
 */
export const calcularPromedio = (materias) => {
  let sumaNotas = 0;
  let cantidadNotas = 0;
  Object.values(materias).forEach(materia => {
    // Considerar notas numéricas válidas cuando la materia está aprobada
    if (materia.estado === STATUS.CURSADA_APROBADA && materia.notaFinal >= 0 && materia.notaFinal <= 10) {
      sumaNotas += materia.notaFinal;
      cantidadNotas++;
    }
  });
  return cantidadNotas > 0 ? (sumaNotas / cantidadNotas).toFixed(2) : "N/A";
};

/**
 * Calcula puntos CPO de manera genérica (ignorando orientación seleccionada)
 * Útil para verificaciones de reglas globales (p.ej. 20 puntos totales para Práctico)
 * @param {Record<string, any>} materias
 * @returns {number}
 */
export const calcularPuntosCPOGenerico = (materias) => {
  if (!materias) return 0;
  let total = 0;
  Object.values(materias).forEach((m) => {
    const esCPO = m.categoria && String(m.categoria).startsWith('CPO');
    const estaAprobadaOCursada =
      m.estado === STATUS.CURSADA_APROBADA ||
      m.notaFinal === -3 ||
      m.notaFinal === -2;
    if (esCPO && estaAprobadaOCursada) {
      total += Number(m.creditos || 0);
    }
  });
  return total;
};

/**
 * @param {string} materiaId
 * @param {any} planCarrera
 * @param {Record<string, any>} materiasOriginales
 * @returns {Set<string>}
 */
export const obtenerPrerrequisitosRecursivos = (materiaId, planCarrera, materiasOriginales) => {
  const prerrequisitos = new Set();
  const materiaActual = materiasOriginales[materiaId] || getMateriaFromPlan(planCarrera, materiaId);
  if (materiaActual && materiaActual.correlativas && materiaActual.correlativas.length > 0) {
    materiaActual.correlativas.forEach((/** @type {string} */ idCorrelativa) => {
      if (!prerrequisitos.has(idCorrelativa)) {
        prerrequisitos.add(idCorrelativa);
        const subPrerrequisitos = obtenerPrerrequisitosRecursivos(idCorrelativa, planCarrera, materiasOriginales);
        subPrerrequisitos.forEach(subId => prerrequisitos.add(subId));
      }
    });
  }
  return prerrequisitos;
};

/**
 * @param {string} materiaIdAprobada
 * @param {any} planCarrera
 * @param {Record<string, any>} materiasOriginales
 * @returns {Set<string>}
 */
export const obtenerMateriasHabilitadasPor = (materiaIdAprobada, planCarrera, materiasOriginales) => {
  const habilitadas = new Set();
  if (!materiasOriginales || !planCarrera || !planCarrera.grupos) return habilitadas;
  
  // 1. Crear un estado simulado donde la materia seleccionada y sus prerrequisitos están cursadas aprobadas
  const estadoSimulado = JSON.parse(JSON.stringify(materiasOriginales));
  if (estadoSimulado[materiaIdAprobada]) {
      estadoSimulado[materiaIdAprobada].estado = STATUS.CURSADA_APROBADA;
      // Marcar también las prerrequisitos de la materiaIdAprobada como cursadas en la simulación
      aprobarCursadasAnterioresRecursivo(materiaIdAprobada, estadoSimulado, planCarrera);
  } else {
      return habilitadas; // Si la materia base no existe, no podemos simular
  }

  // 2. Calcular contadores basados en este estado simulado
  const cbcAprobadoSimulado = Object.values(estadoSimulado).filter(m => m.categoria === 'CBC' && (m.estado === STATUS.CURSADA_APROBADA || m.notaFinal === -3)).length === 6;
  const cpcCursadasSimulado = calcularCursadasAprobadasCPC(estadoSimulado);
  const negritasCursadasSimulado = calcularCursadasAprobadasCPONegritas(estadoSimulado);
  // Para Práctico e Idioma, usar una suma genérica de puntos CPO (ignora orientación)
  const cpoPuntosSimulado = calcularPuntosCPOGenerico(estadoSimulado);


  // 3. Iterar sobre TODAS las materias para ver cuáles se habilitarían con este estadoSimulado
  Object.values(materiasOriginales).forEach(materiaPosibleOriginal => {
    // No considerar la materia seleccionada misma ni las que YA ESTÁN en un estado de progreso EN EL ESTADO SIMULADO
    if (materiaPosibleOriginal.id === materiaIdAprobada || 
        estadoSimulado[materiaPosibleOriginal.id]?.estado === STATUS.CURSADA_APROBADA ||
        estadoSimulado[materiaPosibleOriginal.id]?.estado === STATUS.CURSANDO) {
      return;
    }

    const materiaPosible = estadoSimulado[materiaPosibleOriginal.id]; // Usar la versión del estado simulado para chequear sus correlativas
    if (!materiaPosible) return;


    let correlativasDirectasSimuladasCumplidas = false;
    if (!materiaPosible.correlativas || materiaPosible.correlativas.length === 0) {
        correlativasDirectasSimuladasCumplidas = true;
    } else {
        let directasOk = true;
        for (/** @type {string} */ const idCorr of materiaPosible.correlativas) {
            // Chequear contra el estadoSimulado
            if (!(estadoSimulado[idCorr]?.estado === STATUS.CURSADA_APROBADA)) {
                directasOk = false;
                break;
            }
        }
        correlativasDirectasSimuladasCumplidas = directasOk;
    }

    if (correlativasDirectasSimuladasCumplidas) {
        // Aplicar reglas de ciclo para esta materiaPosible usando los contadores del estadoSimulado
        const puedeIniciarCPOSimuladoConCursadas = cpcCursadasSimulado >= REGLAS_ESPECIALES.CPO_DOCE_MATERIAS;
        const puedeIniciarNegritasSimuladoConCursadas = cpcCursadasSimulado >= REGLAS_ESPECIALES.CPO_DIEZ_MATERIAS;

        let habilitadaPorCicloSimulado = true;
        if (materiaPosible.categoria === 'CPC' && !cbcAprobadoSimulado) {
          habilitadaPorCicloSimulado = false;
        } else if (materiaPosible.categoria && materiaPosible.categoria.startsWith('CPO')) {
          const esObligatoriaComunCPOSimulado = ['uba_21', 'uba_22', 'uba_23', 'uba_24', 'uba_25'].includes(materiaPosible.id);
          if (esObligatoriaComunCPOSimulado && !puedeIniciarNegritasSimuladoConCursadas) {
            habilitadaPorCicloSimulado = false;
          } else if (!esObligatoriaComunCPOSimulado && !puedeIniciarCPOSimuladoConCursadas) {
            habilitadaPorCicloSimulado = false;
          }
        }
        
        let habilitadaPorReglasEspecificasSimulado = true;
        if (materiaPosible.id === 'cpo_practica_profesional') {
            const todasNegritasCursadaSimuladas = negritasCursadasSimulado === 5;
            const cpcCompletoCursadaSimulado = cpcCursadasSimulado >= 14;       
            const veintePuntosCPOSimulado = cpoPuntosSimulado >= 20;      
            if (!todasNegritasCursadaSimuladas || !cpcCompletoCursadaSimulado || !veintePuntosCPOSimulado ) {
                 habilitadaPorReglasEspecificasSimulado = false;
            }
        }
        if (materiaPosible.id === 'cpo_idioma_lectocomprension') {
            const tresNegritasCursadaSimuladas = negritasCursadasSimulado >= 3;
            const cpcCompletoCursadaSimulado = cpcCursadasSimulado >= 14;
            if (!tresNegritasCursadaSimuladas || !cpcCompletoCursadaSimulado) { 
                habilitadaPorReglasEspecificasSimulado = false;
            }
        }

        if(habilitadaPorCicloSimulado && habilitadaPorReglasEspecificasSimulado) {
             habilitadas.add(materiaPosible.id);
        }
    }
  });
  return habilitadas;
};

/**
 * @param {Record<string, any>} materias
 * @returns {{ negritasAprobadas: number, profesionalCompleto: boolean }}
 */
export const calcularNegritasYProfesional = (materias) => {
  let negritasAprobadas = 0;
  let profesionalCompleto = true;

  Object.values(materias).forEach(materia => {
    // Verificar Negritas
    if (materia.esNegrita) {
      if (materia.estado !== STATUS.CURSADA_APROBADA) profesionalCompleto = false;
    }

    // Verificar si todas las materias del profesional están aprobadas
    if (materia.profesionalCompleto && materia.estado !== STATUS.CURSADA_APROBADA) {
      profesionalCompleto = false;
    }
  });

  return {
    negritasAprobadas,
    profesionalCompleto
  };
};

/**
 * Devuelve las materias (ids + metadatos) que se habilitarían si se aprueba materiaIdAprobada
 * @param {string} materiaIdAprobada
 * @param {any} planCarrera
 * @param {Record<string, any>} materiasOriginales
 * @returns {Array<{id:string,nombre:string,categoria:string}>}
 */
export const listarMateriasQueDesbloquea = (materiaIdAprobada, planCarrera, materiasOriginales) => {
  const ids = obtenerMateriasHabilitadasPor(materiaIdAprobada, planCarrera, materiasOriginales);
  const detalles = [];
  planCarrera?.grupos?.forEach((grupo) => {
    grupo?.materias?.forEach((m) => {
      if (ids.has(m.id)) {
        detalles.push({ id: m.id, nombre: m.nombre, categoria: m.categoria });
      }
    });
  });
  return detalles;
};

/**
 * Lista requisitos de una materia con estado de cumplimiento
 * @param {string} materiaId
 * @param {any} planCarrera
 * @param {Record<string, any>} materias
 * @returns {{directos:Array<{id:string,nombre:string,cumplido:boolean}>, reglas:{cbc:boolean,cpc10:boolean,cpc11:boolean,cpc12:boolean}}}
 */
export const getRequisitosDeMateria = (materiaId, planCarrera, materias) => {
  const resultado = {
    directos: [],
    reglas: { cbc: false, cpc10: false, cpc11: false, cpc12: false },
  };
  const m = materias[materiaId] || getMateriaFromPlan(planCarrera, materiaId);
  if (!m) return resultado;

  // Directos (correlativas)
  (m.correlativas || []).forEach((cid) => {
    const ref = materias[cid] || getMateriaFromPlan(planCarrera, cid) || { nombre: cid };
    const cumplido = Boolean(
      materias[cid] &&
        (materias[cid].estado === STATUS.CURSADA_APROBADA)
    );
    resultado.directos.push({ id: cid, nombre: ref.nombre, cumplido });
  });

  // Reglas de ciclo
  const cbcAprobadas = Object.values(materias).filter(
    (x) => x.categoria === 'CBC' && (x.estado === STATUS.CURSADA_APROBADA || x.notaFinal === -3)
  ).length;
  const cpcCursadas = calcularCursadasAprobadasCPC(materias);
  resultado.reglas.cbc = cbcAprobadas === 6;
  resultado.reglas.cpc10 = cpcCursadas >= REGLAS_ESPECIALES.CPO_DIEZ_MATERIAS;
  resultado.reglas.cpc11 = cpcCursadas >= REGLAS_ESPECIALES.CPC_ONCE_MATERIAS;
  resultado.reglas.cpc12 = cpcCursadas >= REGLAS_ESPECIALES.CPO_DOCE_MATERIAS;

  return resultado;
};